import { useMemo, useState } from 'react'
import { EmptyState, ErrorBanner } from '@/components/AppLayout'
import { AttendeeEditor } from '@/features/attendees/AttendeeEditor'
import { ImportPanel } from '@/features/attendees/ImportPanel'
import { RemoveAllAttendeesCard } from '@/features/attendees/RemoveAllAttendeesCard'
import { useAsync } from '@/hooks/useAsync'
import { csvFilename, downloadCsv, toCsv, type CsvColumn } from '@/lib/csv'
import { listSubmissions } from '@/lib/queries'
import {
  createAttendee,
  deleteAttendee,
  listAttendees,
  nextMatchmakingId,
  updateAttendee,
} from '@/lib/queries'
import type { Attendee } from '@/types/database'
import { useEventContext } from './eventContext'

/** Exactly what a nametag needs, in the order a printer expects. */
const NAMETAG_COLUMNS: CsvColumn<Attendee>[] = [
  { header: 'First name', value: (a) => a.first_name },
  { header: 'Last name', value: (a) => a.last_name },
  { header: 'Matchmaking ID', value: (a) => a.matchmaking_id },
]

export function AttendeesPage() {
  const { event } = useEventContext()
  const attendees = useAsync(() => listAttendees(event.id), [event.id])
  // Only to warn accurately about what removing attendees would take with it.
  const submissions = useAsync(() => listSubmissions(event.id), [event.id])

  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState<Attendee | null>(null)
  const [importing, setImporting] = useState(false)
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  // Memoised so the filter below is not recomputed on every unrelated render.
  const rows = useMemo(() => attendees.data ?? [], [attendees.data])

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (needle === '') return rows
    return rows.filter(
      (a) =>
        a.matchmaking_id.includes(needle) ||
        a.first_name.toLowerCase().includes(needle) ||
        (a.last_name ?? '').toLowerCase().includes(needle) ||
        (a.email ?? '').toLowerCase().includes(needle)
    )
  }, [rows, query])

  const activeRows = rows.filter((a) => a.status === 'active')

  async function toggleVisibility(attendee: Attendee) {
    setBusyId(attendee.id)
    setActionError(null)
    try {
      const saved = await updateAttendee(attendee.id, { is_visible: !attendee.is_visible })
      attendees.setData((current) => (current ?? []).map((a) => (a.id === saved.id ? saved : a)))
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not update visibility')
    } finally {
      setBusyId(null)
    }
  }

  async function handleAdd() {
    if (newName.trim() === '') return
    setActionError(null)
    try {
      await createAttendee({
        eventId: event.id,
        matchmakingId: nextMatchmakingId(rows),
        firstName: newName.trim(),
      })
      setNewName('')
      setAdding(false)
      attendees.reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not add the attendee')
    }
  }

  async function handleDelete(attendee: Attendee) {
    // Deleting removes their submissions and any match they were part of, so it
    // is a different action from hiding them, and it asks first.
    const confirmed = window.confirm(
      `Remove ${attendee.first_name} (${attendee.matchmaking_id})? This also deletes their form submission and any matches. To keep their answers but take them off the form, hide them instead.`
    )
    if (!confirmed) return

    setBusyId(attendee.id)
    try {
      await deleteAttendee(attendee.id)
      attendees.reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not remove the attendee')
    } finally {
      setBusyId(null)
    }
  }

  function handleDownload() {
    const forPrint = activeRows.filter((a) => a.is_visible)
    downloadCsv(csvFilename(event.name, 'nametags'), toCsv(forPrint, NAMETAG_COLUMNS))
  }

  return (
    <div className="stack">
      {actionError !== null && <ErrorBanner message={actionError} />}
      {attendees.error !== null && <ErrorBanner message={attendees.error} />}

      <div className="row btn-group-stack">
        <button
          className="btn"
          type="button"
          onClick={() => {
            setImporting((open) => !open)
            setAdding(false)
          }}
        >
          Import from Eventbrite
        </button>
        <button
          className="btn"
          type="button"
          onClick={() => {
            setAdding((open) => !open)
            setImporting(false)
          }}
        >
          Add attendee
        </button>
        <button
          className="btn"
          type="button"
          onClick={handleDownload}
          disabled={activeRows.length === 0}
        >
          Download nametag CSV
        </button>
      </div>

      {importing && (
        <ImportPanel
          eventId={event.id}
          defaultEventbriteEventId={event.eventbrite_event_id}
          onImported={() => {
            attendees.reload()
          }}
          onClose={() => {
            setImporting(false)
          }}
        />
      )}

      {adding && (
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault()
            void handleAdd()
          }}
        >
          <h2>Add an attendee</h2>
          <div className="field">
            <label className="field-label" htmlFor="new-name">
              First name
            </label>
            <p className="field-hint">
              They will get number {nextMatchmakingId(rows) || '—'}. You can change it afterwards.
            </p>
            <input
              id="new-name"
              type="text"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value)
              }}
              autoFocus
              required
            />
          </div>
          <div className="row btn-group-stack">
            <button className="btn btn-primary" type="submit" disabled={newName.trim() === ''}>
              Add
            </button>
          </div>
        </form>
      )}

      {editing !== null && (
        <AttendeeEditor
          // Remounts when a different attendee is picked. The editor seeds its
          // fields from props once, so a reused instance would keep showing the
          // previous person's values — and save them onto the wrong attendee.
          key={editing.id}
          attendee={editing}
          onSaved={(saved) => {
            attendees.setData((current) =>
              (current ?? []).map((a) => (a.id === saved.id ? saved : a))
            )
            setEditing(null)
          }}
          onCancel={() => {
            setEditing(null)
          }}
        />
      )}

      <div className="field">
        <label className="sr-only" htmlFor="attendee-search">
          Search attendees
        </label>
        <input
          id="attendee-search"
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
          }}
          placeholder="Search by name, number or email"
        />
      </div>

      {attendees.loading ? (
        <p className="page-status">Loading attendees…</p>
      ) : rows.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No attendees yet"
            description="Import them from Eventbrite, or add them by hand. Each one gets a 3-digit number for their nametag."
          />
        </div>
      ) : (
        <div className="table-responsive">
          <div className="table-wrap">
            <table>
              <caption className="sr-only">
                Attendees, with nametag number, contact details and whether they appear on the
                public form
              </caption>
              <thead>
                <tr>
                  <th scope="col">No.</th>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Phone</th>
                  <th scope="col">On form</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((attendee) => (
                  <tr
                    key={attendee.id}
                    className={attendee.status === 'cancelled' ? 'row-inactive' : undefined}
                  >
                    <td data-label="No." className="cell-number">
                      {attendee.matchmaking_id}
                    </td>
                    <td data-label="Name">
                      {attendee.first_name} {attendee.last_name ?? ''}
                      {attendee.status === 'cancelled' && (
                        <>
                          {' '}
                          <span className="badge">cancelled</span>
                        </>
                      )}
                      {attendee.locked_fields.length > 0 && (
                        <>
                          {' '}
                          <span
                            className="badge badge-accent"
                            title="Hand-edited fields are kept when you re-import"
                          >
                            edited
                          </span>
                        </>
                      )}
                    </td>
                    <td data-label="Email" className="cell-muted">
                      {attendee.email ?? '—'}
                    </td>
                    <td data-label="Phone" className="cell-muted">
                      {attendee.phone ?? '—'}
                    </td>
                    <td data-label="On form">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={attendee.is_visible}
                          disabled={busyId === attendee.id}
                          onChange={() => void toggleVisibility(attendee)}
                        />
                        <span className="sr-only">
                          Show {attendee.first_name} on the matchmaking form
                        </span>
                      </label>
                    </td>
                    <td data-label="">
                      <div className="row-tight">
                        <button
                          className="btn btn-sm"
                          type="button"
                          onClick={() => {
                            setEditing(attendee)
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          type="button"
                          disabled={busyId === attendee.id}
                          onClick={() => void handleDelete(attendee)}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <RemoveAllAttendeesCard
          eventId={event.id}
          eventName={event.name}
          attendeeCount={rows.length}
          submissionCount={(submissions.data ?? []).length}
          onRemoved={() => {
            attendees.reload()
            submissions.reload()
          }}
        />
      )}

      {filtered.length === 0 && rows.length > 0 && (
        <p className="field-hint">No attendee matches “{query}”.</p>
      )}
    </div>
  )
}
